/**
 * Shareable session links.
 *
 * A session is already multi-device by construction - the server is authoritative, every client
 * resumes from its last seq, and a private session has a join code. What was missing is the boring
 * part that decides whether people actually use it: a link you can send to a phone.
 *
 * The link carries the session id and, for a private session, its join code. Both go in the URL
 * FRAGMENT rather than the query string, and that is deliberate: a fragment is never sent to the
 * server, never lands in an access log or a proxy log, and does not ride along in a Referer header.
 * A join code in a query string would be written to disk on every hop between the sender and the
 * player.
 */

/** What a join link asks the app to do on boot. */
export interface JoinIntent {
  sessionId: string;
  /** Present only for a private session; absent for a public one. */
  joinCode?: string;
}

/** The hash route a join link uses. */
export const JOIN_ROUTE = '/join';

// A session id is a UUID (v7, from the server). Validating the SHAPE here means a malformed or
// hand-edited link fails in the client with a readable message instead of becoming a request.
const SESSION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Join codes are short, unambiguous and uppercase (JoinCode.Generate on the server). Anything else
// is not a code we produced.
const JOIN_CODE = /^[0-9A-Z]{4,16}$/;

/**
 * Reads a join intent out of a location hash, or returns null when the hash is not a join link.
 * Accepts `#/join/<sessionId>` and `#/join/<sessionId>/<joinCode>`.
 */
export function parseJoinLink(hash: string): JoinIntent | null {
  const path = (hash ?? '').replace(/^#/, '');
  if (!path.startsWith(`${JOIN_ROUTE}/`)) {
    return null;
  }

  const [, , sessionId, joinCode, ...extra] = path.split('/');
  if (!sessionId || extra.length > 0 || !SESSION_ID.test(sessionId)) {
    return null;
  }

  if (joinCode === undefined) {
    return { sessionId };
  }

  const code = joinCode.toUpperCase();
  return JOIN_CODE.test(code) ? { sessionId, joinCode: code } : null;
}

/**
 * Builds the link to share. `origin` is the app's own base URL, so the link keeps working whether the
 * app is served at the site root or under a path.
 */
export function buildJoinLink(origin: string, intent: JoinIntent): string {
  const base = origin.replace(/#.*$/, '').replace(/\/+$/, '');
  const code = intent.joinCode ? `/${intent.joinCode.toUpperCase()}` : '';
  return `${base}/#${JOIN_ROUTE}/${intent.sessionId}${code}`;
}

/**
 * Strips a join link back to a plain app URL. Called once the join has been acted on: leaving the
 * code in the address bar means it survives into browser history and into whatever the next person
 * to look at that screen can read.
 */
export function withoutJoinLink(href: string): string {
  return href.replace(/#\/join\/[^#]*$/, '').replace(/#$/, '');
}
